import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, Building2 } from "lucide-react";

export default function PartnerLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user.role === "partner") navigate("/partner");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === "partner") { toast.success("Welcome back!"); navigate("/partner"); }
      else { setError("Partner access required."); }
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="partner-login-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F9F8] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#03045e] flex items-center justify-center mx-auto mb-4"><Building2 className="w-6 h-6 text-white" /></div>
          <h1 className="font-outfit text-2xl font-medium text-[#03045e]" data-testid="partner-login-title">Hospital Partner Portal</h1>
          <p className="text-sm text-slate-500 mt-1">Sign in to manage your referrals</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="partner-login-form">
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-5" data-testid="partner-login-error">{error}</div>}
          <div className="mb-5">
            <Label className="text-sm text-slate-700 mb-1.5 block">Email</Label>
            <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" placeholder="admin@hospital.com" data-testid="partner-email-input" />
            </div>
          </div>
          <div className="mb-6">
            <Label className="text-sm text-slate-700 mb-1.5 block">Password</Label>
            <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" placeholder="Enter password" data-testid="partner-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#03045e] hover:bg-[#020340] text-white h-11" data-testid="partner-login-btn">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
          <p className="text-center text-sm text-slate-400 mt-4">
            <Link to="/" className="text-[#00A4A9] hover:underline">Back to Home</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
